import type { FC } from 'react';
import { lazy, useMemo } from 'react';
import type { LngAllocationsForLsoParams } from 'src/apis/lng-marketing/types';
import { useLngAllocationsForLso } from 'src/apis/lng-marketing/use-lng-allocations-for-lso';
import { type LngMarketingRootRequest } from 'src/apis/lng-marketing/use-lng-marketing-root';
import { PageSpinner } from 'src/components/spinner-container';
import type { Successful } from 'src/hooks/use-axios';
import { useLngAllocationsForLsoPageParams } from 'src/pages/lng/allocations/overview/use-page-params';

const Page = lazy(() => import('./page'));

export const LngAllocationsForOperators: FC<{
  lngMarketingRoot: Successful<LngMarketingRootRequest>;
}> = ({ lngMarketingRoot }) => {
  const pageParams = useLngAllocationsForLsoPageParams();

  const params = useMemo(() => {
    return {
      limit: pageParams.value.pageSize,
      offset: pageParams.value.start,
      terminalId: pageParams.value.terminalId.map((terminal) => terminal.value),
      availableServices: pageParams.value.availableServices,
      shipperOrganisationId: pageParams.value.shipperOrganisationId.map(
        (shipper) => shipper.value
      ),
      lngDealId: pageParams.value.lngDealId,
      offerName: pageParams.value.offerName,
      mwhPriceMin: pageParams.value.mwhPriceMin ?? undefined,
      mwhPriceMax: pageParams.value.mwhPriceMax ?? undefined,
      gasAmountMin: pageParams.value.gasAmountMin ?? undefined,
      gasAmountMax: pageParams.value.gasAmountMax ?? undefined,
      serviceRuntimeStart: pageParams.value.serviceRuntimeStart,
      serviceRuntimeEnd: pageParams.value.serviceRuntimeEnd,
      sortBy: pageParams.value.sortBy,
      sortDirection: pageParams.value.sortDirection,
    } satisfies LngAllocationsForLsoParams;
  }, [pageParams.value]);

  const lngAllocations = useLngAllocationsForLso({
    lngMarketingRoot,
    params,
  });

  if (lngAllocations.response == null) return <PageSpinner />;

  return (
    <Page
      pageParams={pageParams}
      lngAllocations={lngAllocations}
      lngMarketingRoot={lngMarketingRoot}
    />
  );
};
